// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the tidy Assistant chat's chrome state that only the chrome reads (the phone search bar,
// the result count shown in the search field, cached History counts), the React contexts that tell
// a card it sits in the Assistant chat, and the actions the header, list, search, card menu and
// History share. The chat's own state lives in state/AssistantChat.

import Authentication from '@app/features/auth/state/Authentication';
import {
	ARCHIVE_FAILED,
	ARCHIVED_NOTICE,
	CONVERSATION_ARCHIVED,
	DISMISS,
	TIDY_UP_DETAIL,
	tidyUpNotice,
	UNDO,
} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeMessages';
import type {AssistantDrawerConversation} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeModel';
import {
	countToday,
	historyRowsFromEvents,
	selectTidyUp,
} from '@app/features/konvo_assistant/components/chat_chrome/AssistantChatChromeModel';
import AssistantChat from '@app/features/konvo_assistant/state/AssistantChat';
import type {AssistantChatLayout} from '@app/features/konvo_assistant/utils/AssistantChatLayout';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import {goToMessage} from '@app/features/messaging/utils/MessageNavigator';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ArchiveIcon, ArrowCounterClockwiseIcon} from '@app/features/ui/icons/Icons';
import type {AssistantChatKind} from '@konvo/schema/src/domains/konvo/AssistantChat';
import * as SnowflakeUtils from '@konvo/snowflake/src/SnowflakeUtils';
import type {I18n} from '@lingui/core';
import {makeAutoObservable, runInAction} from 'mobx';

const NOTICE_MS = 6000;
/** Refetch a History count for the card menu at most this often. */
const HISTORY_COUNT_TTL_MS = 60_000;

class AssistantChatChromeStore {
	/** Phone: the header is a search field. */
	phoneSearch = false;
	/** Results of the current search (shown in the search field); null while none are known. */
	resultCount: number | null = null;
	/** Bumped to focus the header search field (Ctrl/Cmd+F). */
	focusNonce = 0;
	/** Updates of today per kind, for "History · 12 today" in the card menu. */
	historyToday = new Map<AssistantChatKind, {count: number; at: number}>();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	setPhoneSearch(open: boolean): void {
		this.phoneSearch = open;
		if (!open) AssistantChat.closeSearch();
	}

	setResultCount(count: number | null): void {
		this.resultCount = count;
	}

	requestFocus(): void {
		this.focusNonce++;
	}

	todayCount(kind: AssistantChatKind | null): number | null {
		return kind ? (this.historyToday.get(kind)?.count ?? null) : null;
	}

	/** Prefetches the server's History of a kind (agents) so the card menu can say "12 today". */
	async prefetchHistoryCount(kind: AssistantChatKind): Promise<void> {
		const cached = this.historyToday.get(kind);
		if (cached && Date.now() - cached.at < HISTORY_COUNT_TTL_MS) return;
		this.historyToday.set(kind, {count: cached?.count ?? 0, at: Date.now()});
		const response = await AssistantChat.fetchHistory(kind);
		if (!response) return;
		runInAction(() => {
			this.historyToday.set(kind, {
				count: countToday(historyRowsFromEvents(response.events), new Date()),
				at: Date.now(),
			});
		});
	}
}

export const AssistantChatChrome = new AssistantChatChromeStore();

/** Leaves search everywhere (desktop field, phone search bar). */
export function closeAssistantSearch(): void {
	AssistantChatChrome.setPhoneSearch(false);
	AssistantChat.closeSearch();
	AssistantChatChrome.setResultCount(null);
}

/**
 * Shows a message in the chat: picks a view in which it is not hidden (Archived for archived ones,
 * its conversation when that is folded away), then jumps to it so it loads if needed.
 */
export function jumpToAssistantMessage(
	channelId: string,
	messageId: string,
	layout: Pick<AssistantChatLayout, 'infos' | 'placements' | 'currentConversationId'> | null,
	hint?: {archived?: boolean; conversationId?: string | null},
): void {
	closeAssistantSearch();
	const info = layout?.infos.get(messageId);
	const archived = info?.archived ?? hint?.archived ?? false;
	const conversationId = info?.conversationId ?? hint?.conversationId ?? null;
	if (archived) {
		AssistantChat.setView({type: 'archived'});
	} else if (conversationId && conversationId !== layout?.currentConversationId) {
		AssistantChat.setView({type: 'conversation', id: conversationId});
	} else if (AssistantChat.view.type !== 'all' || layout?.placements.get(messageId)?.type === 'hide') {
		AssistantChat.setView({type: 'all'});
	}
	goToMessage(channelId, messageId);
}

/** Opens a conversation from the list; when none of it is loaded yet, jumps to its first message. */
export function openAssistantConversation(channelId: string, conversation: AssistantDrawerConversation): void {
	AssistantChat.setView({type: 'conversation', id: conversation.id});
	if (conversation.messageIds.length === 0 && conversation.firstMessageId) {
		goToMessage(channelId, conversation.firstMessageId, {flash: false});
	}
}

/** Sends text to Konvo in this chat, as a card's suggested prompts do. */
export function askKonvo(channelId: string, content: string): void {
	const text = content.trim();
	if (!text) return;
	void MessageCommands.send(channelId, {content: text, nonce: SnowflakeUtils.fromTimestamp(Date.now())});
}

function undoNotice(
	i18n: I18n,
	text: string,
	detail: string | null,
	input: {messageIds?: Array<string>; conversationIds?: Array<string>},
): void {
	ToastCommands.createToast({
		type: 'info',
		timeout: NOTICE_MS,
		leading: <ArchiveIcon size={16} />,
		children: text,
		detail: detail ?? undefined,
		dismissLabel: i18n._(DISMISS),
		action: {
			label: i18n._(UNDO),
			icon: <ArrowCounterClockwiseIcon size={15} />,
			onClick: () => {
				void AssistantChat.setArchived(input, false).catch(() => ToastCommands.error(i18n._(ARCHIVE_FAILED)));
			},
			'data-flx': 'konvo-assistant.chat-chrome.undo-archive',
		},
	});
}

/** Archives cards or conversations with a notice that offers Undo. */
export async function archiveWithUndo(
	i18n: I18n,
	input: {messageIds?: Array<string>; conversationIds?: Array<string>},
	what: 'card' | 'conversation',
): Promise<void> {
	try {
		await AssistantChat.setArchived(input, true);
	} catch {
		ToastCommands.error(i18n._(ARCHIVE_FAILED));
		return;
	}
	undoNotice(i18n, i18n._(what === 'card' ? ARCHIVED_NOTICE : CONVERSATION_ARCHIVED), null, input);
}

/** "Tidy up": archives every finished card and every conversation from before yesterday in one call. */
export async function tidyUpAssistantChat(i18n: I18n, layout: AssistantChatLayout): Promise<void> {
	const selection = selectTidyUp(layout, new Date());
	if (selection.total === 0) return;
	const input = {messageIds: selection.messageIds, conversationIds: selection.conversationIds};
	try {
		await AssistantChat.setArchived(input, true);
	} catch {
		ToastCommands.error(i18n._(ARCHIVE_FAILED));
		return;
	}
	undoNotice(i18n, tidyUpNotice(i18n, selection.cards, selection.conversations), i18n._(TIDY_UP_DETAIL), input);
}

export function currentUserId(): string | null {
	return Authentication.currentUserId;
}
